import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@legal-500/legal500-design-system";
import { useSearchableSelect } from "../SearchableSelect/useSearchableSelect";
import type { SelectOption } from "../SearchableSelect/useSearchableSelect";
import type { AsyncSelectOption } from "./AsyncSearchableSelect";
import styles from "./AsyncSearchableSelect.module.css";

type AsyncSearchableMultiSelectProps = {
  name: string;
  label: string;
  value: AsyncSelectOption[];
  onChange: (value: AsyncSelectOption[]) => void;
  onSearch: (query: string) => Promise<AsyncSelectOption[]>;
  disabled?: boolean;
  placeholder?: string;
  debounceMs?: number;
  minSearchLength?: number;
  maxSelected?: number;
};

const NO_OPTIONS: SelectOption[] = [];

export function AsyncSearchableMultiSelect({
  name,
  label,
  value,
  onChange,
  onSearch,
  disabled = false,
  placeholder = "Search...",
  debounceMs = 300,
  minSearchLength = 2,
  maxSelected,
}: AsyncSearchableMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [options, setOptions] = useState<AsyncSelectOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Map<number, HTMLLIElement>>(new Map());
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const close = useCallback(() => setIsOpen(false), []);
  const { dropdownStyle, containerRef, dropdownRef, searchInputRef } = useSearchableSelect({
    options: NO_OPTIONS,
    isOpen,
    onClose: close,
    preserveOptionOrder: true,
  });

  const resetSearch = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setSearchQuery("");
    setOptions([]);
    setHighlightedIndex(-1);
  }, []);

  const performSearch = useCallback(
    async (query: string) => {
      if (query.length < minSearchLength) {
        setOptions([]);
        setHighlightedIndex(-1);
        return;
      }
      setIsLoading(true);
      try {
        setOptions(await onSearch(query));
      } catch (error) {
        console.error("Search failed:", error);
        setOptions([]);
      } finally {
        setHighlightedIndex(-1);
        setIsLoading(false);
      }
    },
    [onSearch, minSearchLength],
  );

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => performSearch(query), debounceMs);
  };

  useEffect(() => {
    if (!isOpen) resetSearch();
  }, [isOpen, resetSearch]);

  useEffect(() => {
    optionRefs.current.get(highlightedIndex)?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setIsOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const isSelected = (option: AsyncSelectOption) =>
    value.some(selected => selected.value === option.value);
  const atCap = maxSelected !== undefined && value.length >= maxSelected;

  const toggle = (option: AsyncSelectOption) => {
    const selected = isSelected(option);
    if (!selected && atCap) return;
    onChange(selected ? value.filter(item => item.value !== option.value) : [...value, option]);
    resetSearch();
    searchInputRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (options.length > 0) setHighlightedIndex(prev => (prev + 1) % options.length);
        break;
      case "ArrowUp":
        event.preventDefault();
        if (options.length > 0) {
          setHighlightedIndex(prev => (prev - 1 + options.length) % options.length);
        }
        break;
      case "Enter":
        event.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < options.length) {
          toggle(options[highlightedIndex]);
        }
        break;
      case "Tab":
        close();
        break;
    }
  };

  const displayValue =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? value[0].label
        : `${value.length} selected`;

  return (
    <div className={styles.formField}>
      <label htmlFor={name} className={styles.label}>
        {label}
      </label>

      <div ref={containerRef} className={styles.selectContainer}>
        <button
          ref={triggerRef}
          type="button"
          id={name}
          className={styles.selectButton}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className={value.length > 0 ? styles.value : styles.placeholder}>
            {displayValue}
          </span>
          <span className={styles.actions}>
            {value.length > 0 && !disabled && (
              <span
                className={styles.clearButton}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  onChange([]);
                }}
                role="button"
                aria-label="Clear selection"
              >
                <Icon icon="cross" size="small" />
              </span>
            )}
            <Icon
              icon={isOpen ? "chevron-up" : "chevron-down"}
              size="small"
              className={styles.arrow}
            />
          </span>
        </button>
      </div>

      {isOpen &&
        createPortal(
          <div ref={dropdownRef} className={styles.dropdown} style={dropdownStyle}>
            <div className={styles.searchContainer}>
              <input
                ref={searchInputRef}
                type="text"
                className={styles.searchInput}
                placeholder={`Type to search (min ${minSearchLength} chars)...`}
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>

            <ul className={styles.optionsList} role="listbox" aria-multiselectable="true">
              {atCap && <li className={styles.noResults}>Up to {maxSelected} selected</li>}
              {isLoading ? (
                <li className={styles.loading}>Searching...</li>
              ) : searchQuery.length < minSearchLength ? (
                <li className={styles.noResults}>
                  Type at least {minSearchLength} characters to search
                </li>
              ) : options.length === 0 ? (
                <li className={styles.noResults}>No results found</li>
              ) : (
                options.map((option, index) => {
                  const selected = isSelected(option);
                  const blocked = atCap && !selected;
                  return (
                    <li
                      key={String(option.value)}
                      ref={el => {
                        if (el) optionRefs.current.set(index, el);
                      }}
                      className={`${styles.option} ${selected ? styles.optionSelected : ""} ${
                        index === highlightedIndex ? styles.optionHighlighted : ""
                      } ${blocked ? styles.optionDisabled : ""}`}
                      onClick={() => toggle(option)}
                      role="option"
                      aria-selected={selected}
                      aria-disabled={blocked || undefined}
                    >
                      <div className={styles.optionContent}>
                        <span className={styles.optionLabel}>{option.label}</span>
                        {option.description && (
                          <span className={styles.optionDescription}>{option.description}</span>
                        )}
                      </div>
                    </li>
                  );
                })
              )}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  );
}
